/* eslint-disable @typescript-eslint/no-non-null-assertion */
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  Injector,
  Input,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import {
  NG_VALUE_ACCESSOR, Validators
} from '@angular/forms';
import { BreakpointsService } from '@sondermind/utilities/services';

import {
  AlphabeticKeys,
  EventActions,
  NUMERIC_KEYS,
  NavigationKeys,
  DropdownOption
} from '@sondermind/iris/dropdown';
import { BaseControlValueAccessor, IIrisFormFieldControl } from '@sondermind/iris/form-helpers';
import { IconColors, IconSizes, IconUsageTypes } from '@sondermind/iris/icon';
import { IrisModalService } from '@sondermind/iris/modal';

import { breakpointSm } from '@sondermind/iris-design-tokens/design-tokens';
import {
  Observable, Subject, debounceTime, filter, fromEvent, map, switchMap, take, takeUntil, tap
} from 'rxjs';

import { sortCategoryOptions } from '../../helpers/option-sorting-helpers';
import { IOptionCategory } from '../../models/option-category.interface';
import { ISelectMenuConfig } from '../../models/select-menu-config.interface';
import { SelectMenuComponent } from './select-menu/select-menu.component';

@Component({
  selector: 'filter-select',
  templateUrl: './select.component.html',
  styleUrls: ['./select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: FilterSelectComponent,
    },
  ]
})
export class FilterSelectComponent<T>
  extends BaseControlValueAccessor
  implements OnInit, OnDestroy, IIrisFormFieldControl {
  constructor(
    injector: Injector,
    private irisModalService: IrisModalService,
    private breakpointService: BreakpointsService,
    @Inject('Window') private window: Window,
    private cdRef: ChangeDetectorRef
  ) { super(injector) }

  @Input() placeholder: string = '';
  /** Used to split options into labeled groups */
  @Input() categorizedOptions: Array<IOptionCategory<T | null>> = [];
  @Input() multiple: boolean = false;
  @Input() multicolumn: boolean = false;
  @Input() hasTopSelection: boolean = false;
  /**
   * Used to compare differently typed options when setting an incoming, preset value
   */
  @Input() compareOptions: (opt1: T, opt2: T) => boolean = (opt1: T, opt2: T) => opt1 === opt2;
  @Input() clearSelectedOptions!: Subject<void>;

  @ViewChild('select') dropdown!: ElementRef;
  @ViewChild('listbox') listbox!: ElementRef;

  /** A flat list of all the options */
  options: Array<DropdownOption<T | null>> = [];
  /** A flat list of all the html elements representing the options */
  flattenedCategorizedOptionsElements: HTMLElement[] = [];
  multiSelectOptions: Array<DropdownOption<T>> = [];
  currentSelection: DropdownOption<T | null> | null = null; // For single selection mode
  currentSelections: Array<DropdownOption<T>> = []; // For multiple selection mode
  currentFocusedValue: T | 'select-all' | null = null;
  currentHoveredValue: T | 'select-all' | null = null;

  /** Used for detecting click and blur events */
  formControlClassName = '';

  /** Handles position of listbox overlay */
  shouldDropUp: boolean = false;

  /** Handles expanded/collapsed state of menu */
  isExpanded: boolean = false;

  /** Handles whether single select value can be cleared */
  isRequired: boolean = false;

  /** Handles typeahead functionality */
  destroyed$ = new Subject<void>();
  focusedElement$ = new Subject<HTMLElement>();

  /** If a list of options is trimmed, allow for 'Show More' */
  showAll: boolean = false;
  hasTrimmedListCategory: boolean = false;

  iconColors = IconColors;
  iconSizes = IconSizes;
  iconUsageTypes = IconUsageTypes;

  ngOnInit(): void {
    this.initOptions(this.categorizedOptions);
    if (!this.multiple) {
      this.initSingleSelect();
    } else {
      this.initMultiSelect();
    }

    this.hasTrimmedListCategory = this.categorizedOptions.some((category) => category.trimmedListCount);
    this.formControlClassName = `${this.formControlName}-filter-select`;
  }

  ngAfterViewInit(): void {
    if (!this.multiple) {
      this.enableTypeahead();
    } else {
      this.onOptionsListChanged();
      this.categorizedOptions = sortCategoryOptions(this.categorizedOptions, this.control, this.showAll, this.hasTopSelection);
    }
  }

  private initOptions(categories: Array<IOptionCategory<T | null>>): void {
    const options: Array<DropdownOption<T | null>> = [];
    this.categorizedOptions = sortCategoryOptions(
      this.categorizedOptions,
      this.control,
      this.showAll,
      this.hasTopSelection
    );

    this.isRequired = this.control.hasValidator(Validators.required);

    categories.forEach((category) => {
      category.options.forEach((option) => {
        options.push(option);
      });
    });

    this.options = options;
  }

  private initSingleSelect(): void {
    const initialValueExists =
      this.control.value !== undefined
      && this.control.value !== null
      && this.control.value !== '';

    const foundOption = this.options.find((option) => this.compareOptions(this.control.value, option.value as T));

    // Default to placeholder text if the FormControl didn't initialize a value
    if (initialValueExists && foundOption) {
      this.currentSelection = foundOption;
    }

    // Listen to FormControl in case it gets updated programmatically
    this.listenForSingleValueChange();
    this.listenForStatusChange();

    // Listen to the clearSelectedOptions subject in case we need to clear the current selection
    // based on filter options changing
    if (this.clearSelectedOptions) {
      this.clearSelectedOptions.pipe(takeUntil(this.destroyed$)).subscribe(() => {
        this.clearSingleSelection();
      });
    }
  }

  private listenForSingleValueChange(): void {
    this.control.valueChanges.pipe(
      takeUntil(this.destroyed$),
      tap((newValue: T) => {
        const option = this.options.find((opt) => opt.value === newValue);
        if (option) {
          this.changeSelection(option);
        }
      })
    ).subscribe();
  }

  private listenForStatusChange(): void {
    this.control.statusChanges.pipe(
      takeUntil(this.destroyed$),
      tap(() => this.cdRef.markForCheck())
    ).subscribe();
  }

  private enableTypeahead(): void {
    this.focusedElement$.pipe(
      switchMap((elem) => this.getSearchTerm$(elem)),
      tap((term) => this.getSearchResults(term)),
      takeUntil(this.destroyed$)
    ).subscribe();

    this.focusedElement$.next(this.dropdown.nativeElement);
  }

  private initMultiSelect(): void {
    const initialValueExists = this.control.value && this.control.value.length;

    this.options.forEach((option) => {
      if (option.value !== null) {
        this.multiSelectOptions.push({
          name: option.name,
          value: option.value as T
        });
      }
    });

    if (initialValueExists) {
      this.setInitialValues();
    }

    this.listenForMultiValueChange();
    this.listenForStatusChange();

    // Listen to the clearSelectedOptions subject in case we need to clear the current selection
    // based on filter options changing
    if (this.clearSelectedOptions) {
      this.clearSelectedOptions.pipe(takeUntil(this.destroyed$)).subscribe(() => {
        this.clearAllSelections();
      });
    }
  }

  private onOptionsListChanged(): void {
    this.flattenedCategorizedOptionsElements = this.flattenCategorizedOptionsElements();

    // Reflatten options
    const options: Array<DropdownOption<T | null>> = [];
    this.categorizedOptions.forEach((category) => {
      category.options.forEach((option) => {
        options.push(option);
      });
    });

    this.options = options;

    const multiSelectOptions: Array<DropdownOption<T>> = [];
    this.options.forEach((option) => {
      if (option.value !== null) {
        multiSelectOptions.push({
          name: option.name,
          value: option.value as T
        });
      }
    });

    this.multiSelectOptions = multiSelectOptions;
  }

  /**
   * Flatten the DOM to directly access the options elements which are under category divs,
   * to navigate through them more easily with a keyboard
   */
  private flattenCategorizedOptionsElements(): HTMLElement[] {
    const resultElements: HTMLElement[] = [];

    // the child nodes of the listbox are sections representing categories that contain options
    const sections: NodeList = this.listbox.nativeElement.childNodes;

    sections.forEach((section: Node) => {
      section.childNodes.forEach((c) => {
        const child = c as HTMLElement;
        // if it has no attributes, it's a comment or text, ignore those
        // also ignore any headers/dividers that are not aria-labelled
        if (child.attributes && child.ariaLabel) {
          resultElements.push(child);
        }
      });
    });

    return resultElements;
  }

  private setInitialValues(): void {
    // If an invalid value is passed during initialization, remove it
    this.multiSelectOptions.forEach((option) => {
      const foundOption: unknown = this.control.value.find((value: any) => this.compareOptions(value, option.value));
      if (foundOption) {
        this.currentSelections.push(option);
      }
    });
  }

  private listenForMultiValueChange(): void {
    this.control.valueChanges.pipe(
      takeUntil(this.destroyed$),
      tap((newValue: T[]) => {
        this.setMultiSelections(newValue);
      })
    ).subscribe();
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  onHoverEnter(value: T): void {
    this.currentHoveredValue = value;
  }

  onHoverLeave(): void {
    this.currentHoveredValue = null;
  }

  getSearchTerm$(targetElem: HTMLElement): Observable<string> {
    let term = '';

    return fromEvent<KeyboardEvent>(targetElem, 'keydown').pipe(
      filter((e: KeyboardEvent) => {
        const alphaKey = e.key as keyof typeof AlphabeticKeys;
        const numKey = +e.key as keyof typeof NUMERIC_KEYS;
        return !!AlphabeticKeys[alphaKey] || !!NUMERIC_KEYS[numKey];
      }),
      map((e: KeyboardEvent) => {
        term += e.key;
        return term;
      }),
      debounceTime(250),
      tap(() => { term = '' }),
      takeUntil(this.destroyed$)
    );
  }

  onBlur(e: FocusEvent): void {
    const elemReceivingFocus = e.relatedTarget as HTMLElement;

    // When the menu is expanded, a click outside of the dropdown list closes the menu
    if (elemReceivingFocus) {
      const isInsideClick = elemReceivingFocus.classList.contains('filter-select-list-item')
        || elemReceivingFocus.classList.contains(this.formControlClassName)
        || elemReceivingFocus.classList.contains('iris-checkbox')
        || elemReceivingFocus.classList.contains('iris-checkbox-box')
        || elemReceivingFocus.classList.contains('iris-indeterminate')
        || elemReceivingFocus.classList.contains('iris-checkmark')
        || elemReceivingFocus.classList.contains('iris-checkmark-half')
        || elemReceivingFocus.classList.contains('show-more-button');

      if (!isInsideClick) {
        this.closeOnBlur();
      }
    } else {
      this.closeOnBlur();
    }
  }

  closeOnBlur(): void {
    // setTimeout to avoid ExpressionChangedAfterItHasBeenCheckedError
    setTimeout(() => {
      this.control.markAsTouched();
    }, 0);
    this.collapseMenu();
  }

  expandClickMenu(e: MouseEvent): void {
    e.stopImmediatePropagation();

    const targetElem = e.target as HTMLElement;
    const isClearIconClicked = targetElem.classList.contains('clear-value-icon');

    if (isClearIconClicked) {
      this.clearSingleSelection();
    } else if (!this.isExpanded) {
      this.expandMenu();
    } else {
      this.collapseMenu();
    }
  }

  expandKeyboardMenu(e: KeyboardEvent): void {
    e.stopImmediatePropagation();

    if (!this.isExpanded) {
      this.expandMenu();

      if (this.control.value) {
        this.focusCurrentSelection();
      } else {
        this.focusListItem(0);
      }
    } else {
      this.collapseMenu();
    }
  }

  expandMenu(): void {
    const isMobile = this.window.innerWidth <= this.parseMobileBreakpoint();

    if (isMobile && this.multiple) {
      this.openMobileMultiMenu();
    } else {
      this.isExpanded = true;
      this.onOptionsListChanged();
    }

    this.shouldDropUp = this.getDropdownPosition();

    // Ensures that focus changes only after the hidden class is removed from the listbox
    setTimeout(() => {
      this.listbox.nativeElement.focus();
      this.flattenedCategorizedOptionsElements = this.flattenCategorizedOptionsElements();
    }, 0);
  }

  getDropdownPosition(): boolean {
    const position = this.dropdown.nativeElement.getBoundingClientRect();
    const viewportCenter = this.window.innerHeight / 2;

    return position.y > viewportCenter;
  }

  private parseMobileBreakpoint(): number {
    const stringWithPx = breakpointSm; // e.g. "959px"
    const { length } = stringWithPx;
    return +stringWithPx.slice(0, length - 2);
  }

  private openMobileMultiMenu(): void {
    /* eslint-disable @typescript-eslint/indent */
    const modalRef = this.irisModalService
      .openUnstyledFullScreenOverlay<SelectMenuComponent<T>, ISelectMenuConfig<T>, T[] | null>(
        SelectMenuComponent<T>,
        {
          categorizedOptions: this.categorizedOptions,
          compareOptionsFn: this.compareOptions,
          label: this.placeholder,
          selections: this.control.value as T[],
          hasTopSelection: this.hasTopSelection,
        },
      );
    /* eslint-enable @typescript-eslint/indent */

    this.breakpointService.toggleModal(true);

    modalRef.afterClosed$().pipe(
      take(1)
    ).subscribe((selections) => {
      if (selections) {
        this.control.setValue(selections);
      }
      this.breakpointService.toggleModal(false);
    });

    this.breakpointService.isMobile$.pipe(
      takeUntil(modalRef.afterClosed$())
    ).subscribe((isMobile) => {
      if (!isMobile) {
        modalRef.close();
      }
    });
  }

  collapseMenu(): void {
    this.currentFocusedValue = null;
    this.isExpanded = false;
    this.showAll = false;
    this.categorizedOptions = sortCategoryOptions(
      this.categorizedOptions,
      this.control,
      this.showAll,
      this.hasTopSelection
    );
  }

  changeSelection(option: DropdownOption<T | null>): void {
    this.currentSelection = option;
    this.updateFormControl(option.value);

    if (this.isExpanded) {
      this.collapseMenu();
      this.redirectFocus(this.dropdown.nativeElement);
    }
  }

  private clearSingleSelection(): void {
    this.currentSelection = null;
    this.control.setValue(null);
  }

  updateFormControl(newValue: T | null): void {
    if (this.control.value !== newValue) {
      this.control.setValue(newValue);
    }
    this.control.markAsTouched();
    this.control.markAsDirty();
  }

  redirectFocus(elem: HTMLElement): void {
    elem.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    elem.focus();

    this.focusedElement$.next(elem);
  }

  focusCurrentSelection(): void {
    const currentItemIndex: number = this.options.findIndex((option) => option.value === this.control.value);
    this.focusListItem(currentItemIndex);
  }

  focusListItem(index: number): void {
    let visibleIndex = index;
    if (this.multiple) {
      if (this.hasTrimmedListCategory) {
        // Extra -1 if not showing all options since the last item in a trimmed list will be the show more button
        const lastShownFilterOptionIndex = this.showAll
          ? this.flattenedCategorizedOptionsElements.length - 1
          : this.flattenedCategorizedOptionsElements.length - 2;
        const lastVisibleIndex = visibleIndex > lastShownFilterOptionIndex && lastShownFilterOptionIndex >= 0
          ? lastShownFilterOptionIndex
          : visibleIndex;

        this.currentFocusedValue = this.multiSelectOptions[lastVisibleIndex].value;
        visibleIndex = lastVisibleIndex;
      } else {
        this.currentFocusedValue = this.multiSelectOptions[index].value;
      }
    } else {
      this.currentFocusedValue = this.options[index].value;
    }

    // Ensures that focus changes only after the hidden class is removed from the listbox
    setTimeout(() => {
      const divToFocus = this.flattenedCategorizedOptionsElements[visibleIndex];
      if (divToFocus) {
        this.redirectFocus(divToFocus);
      }
    }, 0);
  }

  navigateList(direction: NavigationKeys.ArrowDown | NavigationKeys.ArrowUp, action: EventActions): void {
    const currentItemIndex: number = this.isExpanded
      ? this.options.findIndex((option) => option.value === this.currentFocusedValue)
      : this.options.findIndex((option) => option.value === this.control.value);
    let nextItemIndex: number = -1;
    let isEndOfList = true;

    if (direction === NavigationKeys.ArrowDown && currentItemIndex < this.options.length - 1) {
      isEndOfList = false;
      nextItemIndex = currentItemIndex + 1;
    } else if (direction === NavigationKeys.ArrowUp && currentItemIndex > 0) {
      isEndOfList = false;
      nextItemIndex = currentItemIndex - 1;
    }

    const isIndexWithinDisplayedOptions: boolean = nextItemIndex < this.flattenedCategorizedOptionsElements.length;

    if (!isEndOfList && action === EventActions.Focus && isIndexWithinDisplayedOptions) {
      const nextItemElem = this.flattenedCategorizedOptionsElements[nextItemIndex];
      this.redirectFocus(nextItemElem);
      this.currentFocusedValue = this.options[nextItemIndex].value;
    } else if (!isEndOfList && action === EventActions.Select) {
      const nextOption = this.options[nextItemIndex];
      this.changeSelection(nextOption);
    }
  }

  escapeMenu(e: Event): void {
    e.stopPropagation();

    this.collapseMenu();
    this.redirectFocus(this.dropdown.nativeElement);
  }

  handleKeyDown(e: KeyboardEvent): void {
    if (NavigationKeys[e.key as keyof typeof NavigationKeys]) {
      this.handleKeyNavigation(e);
    }
  }

  handleKeyNavigation(e: KeyboardEvent): void {
    switch (e.key) {
      case NavigationKeys.Tab:
        if (this.isExpanded) {
          this.collapseMenu();
        }
        break;
      case NavigationKeys.Enter:
      case NavigationKeys[' ']:
        if (this.isExpanded) {
          e.preventDefault();
          const option = this.options.find((opt) => opt.value === this.currentFocusedValue);
          if (option) {
            this.changeSelection(option);
          }
        } else {
          this.expandKeyboardMenu(e);
        }
        break;
      case NavigationKeys.Escape:
        // Prevents browser window minimization on keydown, while menu collapse is
        // handled on keyup to prevent event from bubbling up to modals
        e.preventDefault();
        break;
      case NavigationKeys.Home:
        if (this.isExpanded) {
          e.preventDefault();
          this.focusListItem(0);
        }
        break;
      case NavigationKeys.End:
        if (this.isExpanded) {
          e.preventDefault();
          this.focusListItem(this.options.length - 1);
        }
        break;
      case NavigationKeys.ArrowUp:
        e.preventDefault();
        this.isExpanded
          ? this.navigateList(NavigationKeys.ArrowUp, EventActions.Focus)
          : this.navigateList(NavigationKeys.ArrowUp, EventActions.Select);
        break;
      case NavigationKeys.ArrowDown:
        e.preventDefault();
        this.isExpanded
          ? this.navigateList(NavigationKeys.ArrowDown, EventActions.Focus)
          : this.navigateList(NavigationKeys.ArrowDown, EventActions.Select);
        break;
      default:
        // eslint-disable-next-line no-useless-return
        return;
    }
  }

  getSearchResults(term: string): void {
    const match = this.findMatch(term);

    if (match && this.isExpanded) {
      const optionIndex: number = this.options.findIndex((option) => option.value === match.value);
      this.focusListItem(optionIndex);
    } else if (match && !this.isExpanded) {
      this.changeSelection(match);
      this.cdRef.markForCheck();
    }
  }

  findMatch(term: string): DropdownOption<T | null> | null {
    let counter = 0;
    let copy = this.options;
    let filteredOptions: Array<DropdownOption<T | null>> = [];

    if (term) {
      while (counter < term.length) {
        // eslint-disable-next-line @typescript-eslint/no-loop-func
        filteredOptions = copy.filter((option) => option.name.toLowerCase()[counter] === term.toLowerCase()[counter]);

        if (filteredOptions.length) {
          copy = filteredOptions;
          counter += 1;
        } else if (!filteredOptions.length && !counter) {
          return null;
        } else {
          // eslint-disable-next-line consistent-return
          return copy[0];
        }
      }
    }

    if (filteredOptions.length) {
      // eslint-disable-next-line consistent-return
      return filteredOptions[0];
    }

    return null;
  }

  // Functions for multiple selection mode below
  expandClickMultiSelectMenu(e: MouseEvent): void {
    e.stopImmediatePropagation();
    const targetElem = e.target as HTMLElement;
    const isClearIconClicked = targetElem.classList.contains('clear-value-icon');

    if (isClearIconClicked) {
      this.clearAllSelections();
    } else if (!this.isExpanded) {
      this.expandMenu();
    } else {
      this.collapseMenu();
    }
  }

  expandKeyboardMultiMenu(e: KeyboardEvent): void {
    e.stopImmediatePropagation();

    if (!this.isExpanded) {
      this.expandMenu();

      if (this.control.value.length) {
        this.focusLastSelection();
      } else {
        this.focusListItem(0);
      }
    } else {
      this.collapseMenu();
    }
  }

  focusLastSelection(): void {
    const lastSelectionIndex = this.control.value.length - 1;
    const currentItemIndex: number = this.multiSelectOptions.findIndex(
      (option) => option.value === this.control.value[lastSelectionIndex]
    );

    this.focusListItem(currentItemIndex);
  }

  changeMultiSelection(option: DropdownOption<T>): void {
    const foundOption: DropdownOption<T> | undefined = this.currentSelections.find(
      (selection: DropdownOption<T>) => this.compareOptions(selection.value, option.value)
    );
    if (!foundOption) {
      this.currentSelections.push(option);
    } else {
      this.currentSelections = this.currentSelections.filter((opt) => opt !== option);
    }
    this.updateMultiFormControl(option.value);
  }

  updateMultiFormControl(newValue: T): void {
    const foundOption: unknown = this.control.value.find((val: T) => this.compareOptions(val, newValue));
    if (!foundOption) {
      this.control.setValue([...this.control.value, newValue]);
    } else {
      const newSelections = this.control.value.filter((val: T) => !this.compareOptions(val, newValue));
      this.control.setValue(newSelections);
    }
    this.control.markAsTouched();
    this.control.markAsDirty();
  }

  private clearAllSelections(): void {
    this.currentSelections = [];
    this.control.setValue([]);
  }

  setMultiSelections(newValue: T[]): void {
    // Clear the previous selections
    this.currentSelections = [];
    const newOptions: Array<DropdownOption<T>> = [];
    newValue.forEach((selection) => {
      const option = this.multiSelectOptions.find((opt) => opt.value === selection);
      if (option) {
        newOptions.push(option);
      }
    });
    this.currentSelections = newOptions;
  }

  handleMultiSelectKeyDown(e: KeyboardEvent): void {
    if (NavigationKeys[e.key as keyof typeof NavigationKeys]) {
      this.isExpanded ? this.handleMultiKeyNavigation(e) : this.handleMultiMenuOpen(e);
    }
  }

  handleMultiMenuOpen(e: KeyboardEvent): void {
    if (e.key === NavigationKeys.Enter || e.key === NavigationKeys[' ']) {
      this.expandKeyboardMultiMenu(e);
    }
  }

  handleShowMoreButtonKeyDown(e: KeyboardEvent): void {
    if (e.key === NavigationKeys.Enter || e.key === NavigationKeys[' ']) {
      e.preventDefault();
      e.stopImmediatePropagation();
      this.control.markAsTouched();

      // Move focus away from any option, since they're about to get rearranged.
      this.dropdown.nativeElement.focus();
      this.showAllOptions();

      // Set timeout to ensure these happen after the view updates.
      setTimeout(() => {
        this.flattenedCategorizedOptionsElements = this.flattenCategorizedOptionsElements();
        // Set focus on the first item in the list
        this.flattenedCategorizedOptionsElements[0].focus();
        this.currentFocusedValue = this.options[0].value;
      }, 0);
    } else {
      this.handleKeyNavigation(e);
    }
  }

  handleMultiKeyNavigation(e: KeyboardEvent): void {
    switch (e.key) {
      case NavigationKeys.Tab:
        this.collapseMenu();
        break;
      case NavigationKeys.Enter:
      case NavigationKeys[' ']:
        e.preventDefault();
        this.handleMultiSelection();
        break;
      case NavigationKeys.Escape:
        // Prevents browser window minimization on keydown, while menu collapse is
        // handled on keyup to prevent event from bubbling up to modals
        e.preventDefault();
        break;
      case NavigationKeys.Home:
        e.preventDefault();
        this.focusListItem(0);
        break;
      case NavigationKeys.End:
        e.preventDefault();
        this.focusListItem(this.multiSelectOptions.length - 1);
        break;
      case NavigationKeys.ArrowUp:
        e.preventDefault();
        this.navigateList(NavigationKeys.ArrowUp, EventActions.Focus);
        break;
      case NavigationKeys.ArrowDown:
        e.preventDefault();
        this.navigateList(NavigationKeys.ArrowDown, EventActions.Focus);
        break;
      default:
        // eslint-disable-next-line no-useless-return
        return;
    }
  }

  handleMultiSelection(): void {
    if (this.currentFocusedValue !== null) {
      const option = this.multiSelectOptions.find((opt) => opt.value === this.currentFocusedValue);
      if (option) {
        this.changeMultiSelection(option);
      }
    }
  }

  /** Expands every category's list of options */
  showAllOptions(): void {
    this.showAll = true;
    this.categorizedOptions = sortCategoryOptions(this.categorizedOptions, this.control, this.showAll, false);
    this.onOptionsListChanged();
  }
}
